import React, { useMemo, useState } from 'react';
import { Money, Bank } from 'phosphor-react';
import { useFetch, useApi } from '_api';
import { formatVND, formatDate, toYmd, useCapabilities } from '_helpers';
import { PageHeader, Bezel, Table, StatusPill, Tag, Select, PeriodPicker } from '_ui';
import { InvoiceDetail } from '_components/InvoiceDetail';

const now = new Date();

function itemsSummary(items = []) {
    if (!items.length) return '—';
    const first = `${items[0].name} ×${items[0].quantity}`;
    return items.length > 1 ? `${first} +${items.length - 1}` : first;
}

export function GiaoDich() {
    const [view, setView] = useState({ mode: 'month', date: toYmd(now), period: { month: now.getMonth() + 1, year: now.getFullYear() } });
    const [employeeId, setEmployeeId] = useState('');
    const [selected, setSelected] = useState(null);
    const { canDeleteTransaction, canVerifyPayment } = useCapabilities();
    const api = useApi();

    // includeManagers=true — a manager can log their own sales too (see transaction.js), and
    // those rows need a name to show in this list instead of a blank "—".
    const { data: employees } = useFetch('/api/employees?includeManagers=true');
    const empMap = useMemo(() => {
        const m = new Map();
        (employees || []).forEach(e => m.set(String(e._id), e.role === 'manager' ? `${e.name} (Quản lý)` : e.name));
        return m;
    }, [employees]);
    const empOptions = [{ value: '', label: 'Tất cả nhân viên' }, ...(employees || []).map(e => ({ value: e._id, label: e.role === 'manager' ? `${e.name} (Quản lý)` : e.name }))];

    const query = view.mode === 'day' ? `date=${view.date}` : `month=${view.period.month}&year=${view.period.year}`;
    const path = `/api/transactions?${query}` + (employeeId ? `&employeeId=${employeeId}` : '');
    const { data, loading, refetch } = useFetch(path);

    async function handleDelete(id) {
        const { ok } = await api.delete(`/api/transactions/${id}`);
        if (ok) { setSelected(null); refetch(); }
    }

    async function handleVerifyPaid(id) {
        const { ok } = await api.patch(`/api/transactions/${id}/verify-paid`);
        if (ok) { setSelected(null); refetch(); }
    }
    const rows = data || [];

    const columns = [
        { key: 'createdAt', header: 'Ngày', render: r => <span className="tnum text-espresso-soft">{formatDate(r.createdAt)}</span> },
        { key: 'employee', header: 'Nhân viên', render: r => empMap.get(String(r.employee_ref)) || <span className="text-espresso-soft/50">—</span> },
        { key: 'customer', header: 'Khách', render: r => r.customerName || <span className="text-espresso-soft/50">—</span> },
        { key: 'items', header: 'Dịch vụ', render: r => <span className="text-espresso-soft">{itemsSummary(r.items)}</span> },
        { key: 'method', header: 'Hình thức', render: r => (
            <Tag tone="neutral"><span className="inline-flex items-center gap-1.5">{r.paymentMethod === 'cash' ? <Money size={13} /> : <Bank size={13} />}{r.paymentMethod === 'cash' ? 'Tiền mặt' : 'Chuyển khoản'}</span></Tag>
        ) },
        { key: 'total', header: 'Tiền', align: 'right', render: r => <span className="tnum font-medium">{formatVND(r.totalPrice)}</span> },
        { key: 'status', header: 'Trạng thái', align: 'right', render: r => <StatusPill status={r.status} pendingLabel="Chờ thu" paidLabel="Đã thu" /> },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Giao dịch"
                description="Toàn bộ hoá đơn theo nhân viên, theo ngày hoặc theo tháng."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        <Select className="min-w-[12rem]" value={employeeId} onChange={setEmployeeId} options={empOptions} />
                        <PeriodPicker value={view} onChange={setView} />
                    </div>
                }
            />
            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} onRowClick={setSelected}
                    empty={view.mode === 'day' ? 'Không có giao dịch nào trong ngày.' : 'Không có giao dịch nào trong tháng.'} />
            </Bezel>

            <InvoiceDetail
                invoice={selected}
                onClose={() => setSelected(null)}
                employeeName={selected ? empMap.get(String(selected.employee_ref)) : undefined}
                onDelete={canDeleteTransaction ? handleDelete : undefined}
                onVerifyPaid={canVerifyPayment ? handleVerifyPaid : undefined}
            />
        </div>
    );
}
